import { Vector2 } from 'three';
import type { BufferGeometry } from 'three';
import type { Float32BufferAttribute } from 'three';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';

import { MATERIAL_PROPERTIES } from '../constants';
import type { STEPBody } from '../loaders/step/STEPBody';

const DEFAULT_RESOLUTION = new Vector2(1, 1);

/**
 * Extracts line segment positions from an indexed BufferGeometry.
 * Converts indexed vertex data into a flat Float32Array of segment positions,
 * where each segment is represented by 6 consecutive floats (start xyz, end xyz).
 * If the geometry is not indexed, returns the position array directly.
 */
const segmentPositionsFromIndexedGeometry = (
  geometry: BufferGeometry,
): Float32Array => {
  const position = geometry.attributes.position as Float32BufferAttribute;
  const posArray = position.array;
  const index = geometry.index;
  if (!index) {
    return posArray as Float32Array;
  }
  const indexArray = index.array;
  const segmentCount = index.count / 2;
  const out = new Float32Array(segmentCount * 6);
  for (let i = 0; i < segmentCount; i++) {
    const a = indexArray[i * 2] * 3;
    const b = indexArray[i * 2 + 1] * 3;
    out[i * 6] = posArray[a];
    out[i * 6 + 1] = posArray[a + 1];
    out[i * 6 + 2] = posArray[a + 2];
    out[i * 6 + 3] = posArray[b];
    out[i * 6 + 4] = posArray[b + 1];
    out[i * 6 + 5] = posArray[b + 2];
  }
  return out;
};

/**
 * Extracts line segment colors from an edge BufferGeometry.
 * Converts RGBA vertex colors (4 components per vertex) into a flat Float32Array
 * of RGB segment colors (6 floats per segment: start RGB, end RGB).
 * If no color attribute exists, returns an array of zeros (black).
 */
const segmentColorsFromEdgeGeometry = (
  geometry: BufferGeometry,
): Float32Array => {
  const colorAttr = geometry.attributes.color as Float32BufferAttribute;
  const colorArray = colorAttr?.array;
  if (!colorArray || colorArray.length < 8) {
    const segmentCount = geometry.index
      ? geometry.index.count / 2
      : geometry.attributes.position.count / 2;
    const out = new Float32Array(segmentCount * 6);
    for (let i = 0; i < segmentCount * 6; i++) out[i] = 0;
    return out;
  }
  const index = geometry.index;
  const segmentCount = index ? index.count / 2 : colorArray.length / 4 / 2;
  const out = new Float32Array(segmentCount * 6);
  for (let i = 0; i < segmentCount; i++) {
    const va = index ? index.array[i * 2] * 4 : i * 8;
    const vb = index ? index.array[i * 2 + 1] * 4 : i * 8 + 4;
    out[i * 6] = colorArray[va];
    out[i * 6 + 1] = colorArray[va + 1];
    out[i * 6 + 2] = colorArray[va + 2];
    out[i * 6 + 3] = colorArray[vb];
    out[i * 6 + 4] = colorArray[vb + 1];
    out[i * 6 + 5] = colorArray[vb + 2];
  }
  return out;
};

/**
 * Creates LineSegments2 objects ("fat lines") from edge geometries for DXF rendering.
 * Fat lines (LineSegments2) maintain consistent visual thickness regardless of zoom level,
 * unlike standard Three.js LineSegments which are always 1 pixel wide.
 * Each edge geometry is converted into a LineSegments2 with its own LineMaterial.
 */
export const createFlatPartEdgeLineSegments2 = (
  edgeGeometries: BufferGeometry[],
  linewidth: number = MATERIAL_PROPERTIES.DXF_PART_LINEWIDTH,
): LineSegments2[] => {
  const result: LineSegments2[] = [];
  for (const geometry of edgeGeometries) {
    const positions = geometry.index
      ? segmentPositionsFromIndexedGeometry(geometry)
      : (geometry.attributes.position.array as Float32Array);
    if (positions.length < 6) continue;
    const lineGeometry = new LineSegmentsGeometry();
    lineGeometry.setPositions(positions);
    const colors = segmentColorsFromEdgeGeometry(geometry);
    lineGeometry.setColors(colors);
    const material = new LineMaterial({
      color: 0xffffff,
      linewidth,
      resolution: DEFAULT_RESOLUTION.clone(),
    });
    material.defines = material.defines || {};
    material.defines.USE_COLOR = '';
    const lineSegments2 = new LineSegments2(lineGeometry, material);
    result.push(lineSegments2);
  }
  return result;
};

/**
 * Synchronizes the colors of LineSegments2 objects with their source edge geometries.
 * Called when edge colors change (e.g., during selection highlighting) to ensure
 * the fat lines display the updated colors. Extracts colors from the original
 * edge geometries and applies them to the corresponding LineSegments2 objects.
 */
export const syncFlatPartLineSegments2Colors = (stepBody: STEPBody): void => {
  const lineSegments2List = stepBody.edgeLineSegments2;
  if (
    !lineSegments2List?.length ||
    stepBody.edgeGeometries.length !== lineSegments2List.length
  )
    return;
  for (let i = 0; i < stepBody.edgeGeometries.length; i++) {
    const edgeGeometry = stepBody.edgeGeometries[i];
    const lineSegments2 = lineSegments2List[i];
    const colors = segmentColorsFromEdgeGeometry(edgeGeometry);
    const lineGeom = lineSegments2.geometry as LineSegmentsGeometry;
    lineGeom.setColors(colors);
    const startAttr = lineGeom.attributes.instanceColorStart as {
      needsUpdate?: boolean;
    };
    const endAttr = lineGeom.attributes.instanceColorEnd as {
      needsUpdate?: boolean;
    };
    if (startAttr) startAttr.needsUpdate = true;
    if (endAttr) endAttr.needsUpdate = true;
  }
};
